import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import DescriptionIcon from "@mui/icons-material/Description";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { postContenidosMasivo, getLoteMaximo, stripBase64Prefix } from "../../../helpers/ContenidosData";
import { postDigitalizacionesCaratulas } from "../../../helpers/DigitalizacionData";
import { SnackbarMessenger } from "../../../../../general_components/SnackbarMessenger";

const CARATULA_CHUNK = 5;

export const StepConfirmar = ({ state, dispatch, idCaja, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [contenidoResults, setContenidoResults] = useState(null); // null = not started
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const activeContenidos = (state.id_ocr_plantilla || state.regiones?.length > 0)
    ? state.contenidos.filter((c) => c.status === "success")
    : state.contenidos;

  const failedOcrCount = (state.id_ocr_plantilla || state.regiones?.length > 0)
    ? state.contenidos.filter((c) => c.status === "error").length
    : 0;

  const selectedTipoLabel = state.id_tipo_contenidos || "-";
  const selectedPlantillaLabel = state.id_ocr_plantilla ? `Plantilla #${state.id_ocr_plantilla}` : "Sin plantilla OCR";

  const resolveMetadataForContenido = (contenidoId) => {
    const result = [];
    (state.metadatosDefinition || []).forEach((meta) => {
      const mode = state.fieldModes?.[meta.id] || "manual";
      let valor = "";
      if (mode === "manual") {
        valor = state.manualValues?.[meta.id] || "";
      } else {
        valor = state.contenidoMetadata?.[contenidoId]?.[meta.id] || "";
      }
      result.push({ id: "", id_metadatos: String(meta.id), valor });
    });
    return result;
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    const total = activeContenidos.length;
    setProgress({ done: 0, total });

    // Step 1: get lote base
    let loteBase = 1;
    try {
      loteBase = await getLoteMaximo(idCaja);
    } catch {
      loteBase = 1;
    }

    // Step 2: build contenidos payloads
    const contenidosPayloads = activeContenidos.map((c, idx) => ({
      id: "",
      id_cajas: idCaja,
      id_tipo_contenidos: state.id_tipo_contenidos,
      lote: String(loteBase + idx),
      meta_datos: resolveMetadataForContenido(c.contenidoId),
    }));

    // Step 3: create contenidos sequentially
    const createdResults = await postContenidosMasivo(contenidosPayloads);

    // Step 4: build image upload items for ALL images of each created contenido
    const imageItems = [];
    createdResults.forEach((res, idx) => {
      if (res.contenidoId && activeContenidos[idx]) {
        activeContenidos[idx].images.forEach((img, imgIdx) => {
          imageItems.push({
            modelo_padre: "contenidos",
            id_modelo_padre: res.contenidoId,
            image: stripBase64Prefix(img.base64),
            save: false,
            _contenidoIdx: idx,
            _isFirst: imgIdx === 0,
          });
        });
      }
    });

    const finalResults = activeContenidos.map((c, idx) => ({
      contenidoId: c.contenidoId,
      imageCount: c.images.length,
      status: "pending",
      createdContenidoId: createdResults[idx]?.contenidoId || null,
      error: createdResults[idx]?.error || null,
    }));

    // Upload images in chunks of CARATULA_CHUNK
    for (let i = 0; i < imageItems.length; i += CARATULA_CHUNK) {
      const chunk = imageItems.slice(i, i + CARATULA_CHUNK);
      const caratulaPayload = chunk.map((item) => ({
        modelo_padre: item.modelo_padre,
        id_modelo_padre: item.id_modelo_padre,
        image: item.image,
        save: item.save,
      }));

      try {
        await postDigitalizacionesCaratulas(caratulaPayload);
        // Mark affected contenidos as created
        chunk.forEach((item) => {
          const idx = item._contenidoIdx;
          if (finalResults[idx] && finalResults[idx].status === "pending") {
            finalResults[idx].status = "created";
          }
        });
      } catch (err) {
        chunk.forEach((item) => {
          const idx = item._contenidoIdx;
          if (finalResults[idx] && finalResults[idx].status === "pending") {
            finalResults[idx].status = "partial";
            finalResults[idx].error = `Contenido creado pero imagen no guardada: ${err?.message || "error desconocido"}`;
          }
        });
      }

      setProgress({ done: Math.min(i + CARATULA_CHUNK, imageItems.length), total });
    }

    // Mark contenidos where creation itself failed
    finalResults.forEach((r, idx) => {
      if (!r.createdContenidoId) {
        finalResults[idx].status = "failed";
      } else if (r.status === "pending") {
        finalResults[idx].status = "created";
      }
    });

    setContenidoResults(finalResults);
    setProgress({ done: total, total });
    setSubmitting(false);

    const createdCount = finalResults.filter((r) => r.status === "created" || r.status === "partial").length;
    const failedCount = finalResults.filter((r) => r.status === "failed").length;

    if (createdCount === total) {
      setSnackbarMessage(`${createdCount} contenido${createdCount !== 1 ? "s" : ""} creado${createdCount !== 1 ? "s" : ""} correctamente.`);
      setSnackbarSeverity("success");
    } else if (createdCount > 0) {
      setSnackbarMessage(
        `${createdCount} creados, ${failedCount} fallidos${failedOcrCount > 0 ? `, ${failedOcrCount} excluidos por OCR fallido` : ""}.`
      );
      setSnackbarSeverity("warning");
    } else {
      setSnackbarMessage("No se pudo crear ningun contenido.");
      setSnackbarSeverity("error");
    }
    setSnackbarVisible(true);

    if (createdCount > 0 && onSuccess) {
      setTimeout(() => onSuccess(), 1500);
    }
  };

  const canConfirm = !submitting && activeContenidos.length > 0 && !contenidoResults;

  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Step header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F", mb: 0.5 }}>
          Confirmar creacion masiva
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Revisa el resumen antes de confirmar. Esta accion creara los contenidos en la caja.
        </Typography>
      </Box>

      {/* Summary cards */}
      {!contenidoResults && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#90caf9", backgroundColor: "#f8fbff" }}>
              <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <DescriptionIcon sx={{ color: "#1565C0", fontSize: 20 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#1565C0" }}>
                    Tipo de Contenido
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F" }}>
                  #{selectedTipoLabel}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: state.id_ocr_plantilla ? "#a5d6a7" : "#ffcc80",
                backgroundColor: state.id_ocr_plantilla ? "#f1f8f1" : "#fffde7",
              }}
            >
              <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <DocumentScannerIcon sx={{ color: state.id_ocr_plantilla ? "#2e7d32" : "#ed6c02", fontSize: 20 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: state.id_ocr_plantilla ? "#2e7d32" : "#ed6c02" }}>
                    Plantilla OCR
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: state.id_ocr_plantilla ? "#1b5e20" : "#e65100" }}>
                  {selectedPlantillaLabel}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#a5d6a7", backgroundColor: "#f1f8f1" }}>
              <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#2e7d32" }}>
                    Contenidos a crear
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#1b5e20", lineHeight: 1 }}>
                  {activeContenidos.length}
                </Typography>
                {failedOcrCount > 0 && (
                  <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", mt: 0.5 }}>
                    {failedOcrCount} excluido{failedOcrCount !== 1 ? "s" : ""} por OCR fallido
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* OCR failures warning */}
      {failedOcrCount > 0 && !contenidoResults && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <strong>{failedOcrCount} contenido{failedOcrCount !== 1 ? "s" : ""} excluido{failedOcrCount !== 1 ? "s" : ""}</strong> por error en el procesamiento OCR. Solo se crearan los {activeContenidos.length} contenidos con OCR exitoso.
        </Alert>
      )}

      {/* No contenidos error */}
      {!contenidoResults && !submitting && activeContenidos.length === 0 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          No hay contenidos para crear. Vuelve atras y verifica las imagenes.
        </Alert>
      )}

      {/* Processing progress */}
      {submitting && (
        <Paper
          variant="outlined"
          sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: "#90caf9", backgroundColor: "#f8fbff", textAlign: "center" }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Creando contenidos...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {progress.done} de {progress.total} procesados
          </Typography>
          <LinearProgress
            variant={progress.total > 0 ? "determinate" : "indeterminate"}
            value={progressPct}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: "#e3f2fd",
              "& .MuiLinearProgress-bar": {
                background: "linear-gradient(90deg, #1565C0 0%, #42a5f5 100%)",
                borderRadius: 5,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
            {progressPct}%
          </Typography>
        </Paper>
      )}

      {/* Confirm button */}
      {!contenidoResults && canConfirm && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!canConfirm}
          size="large"
          startIcon={<PlayArrowIcon />}
          sx={{
            background: "linear-gradient(135deg, #1565C0 0%, #1976d2 100%)",
            px: 4,
            py: 1.25,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          Confirmar y crear {activeContenidos.length} contenido{activeContenidos.length !== 1 ? "s" : ""}
        </Button>
      )}

      {/* Results */}
      {contenidoResults && (
        <Box>
          {/* Result summary chips */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
            {contenidoResults.filter((r) => r.status === "created" || r.status === "partial").length > 0 && (
              <Paper
                variant="outlined"
                sx={{ px: 2, py: 1.5, borderRadius: 2, borderColor: "#a5d6a7", backgroundColor: "#f1f8f1", display: "flex", alignItems: "center", gap: 1 }}
              >
                <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32", lineHeight: 1 }}>
                    {contenidoResults.filter((r) => r.status === "created" || r.status === "partial").length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">creados</Typography>
                </Box>
              </Paper>
            )}
            {contenidoResults.filter((r) => r.status === "failed").length > 0 && (
              <Paper
                variant="outlined"
                sx={{ px: 2, py: 1.5, borderRadius: 2, borderColor: "#ef9a9a", backgroundColor: "#fff5f5", display: "flex", alignItems: "center", gap: 1 }}
              >
                <ErrorIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f", lineHeight: 1 }}>
                    {contenidoResults.filter((r) => r.status === "failed").length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">fallidos</Typography>
                </Box>
              </Paper>
            )}
            {contenidoResults.filter((r) => r.status === "partial").length > 0 && (
              <Chip
                icon={<WarningAmberIcon />}
                label={`${contenidoResults.filter((r) => r.status === "partial").length} parciales (sin imagenes)`}
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <List dense disablePadding>
              {contenidoResults.map((res, idx) => {
                const isCreated = res.status === "created";
                const isPartial = res.status === "partial";
                const isFailed = res.status === "failed";
                const isPending = res.status === "pending";

                return (
                  <ListItem
                    key={res.contenidoId}
                    divider={idx < contenidoResults.length - 1}
                    sx={{
                      py: 1,
                      backgroundColor:
                        isCreated ? "#f1f8f1" :
                        isPartial ? "#fffde7" :
                        isFailed ? "#fff5f5" :
                        "#fafafa",
                      transition: "background-color 0.15s",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {isCreated && <CheckCircleIcon sx={{ color: "#2e7d32" }} fontSize="small" />}
                      {isPartial && <CheckCircleIcon sx={{ color: "#ed6c02" }} fontSize="small" />}
                      {isFailed && <ErrorIcon color="error" fontSize="small" />}
                      {isPending && <PendingIcon color="disabled" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          #{idx + 1} ({activeContenidos[idx]?.images?.length ?? 1} imagen{(activeContenidos[idx]?.images?.length ?? 1) !== 1 ? "es" : ""})
                        </Typography>
                      }
                      secondary={
                        res.error ? (
                          <Typography variant="caption" color={isPartial ? "warning.main" : "error"}>
                            {res.error}
                          </Typography>
                        ) : isCreated ? (
                          <Typography variant="caption" color="success.main">
                            Creado correctamente (ID: {res.createdContenidoId})
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Box>
      )}

      <SnackbarMessenger
        open={snackbarVisible}
        message={snackbarMessage}
        severity={snackbarSeverity}
        close={() => setSnackbarVisible(false)}
      />
    </Box>
  );
};
